// src/pages/TicketDetail.js
import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getTicket, updateTicket, addResponse, suggestResponse, getAgents, getTicketComments, addTicketComment } from "../api";
import { ArrowLeft, Sparkles, Send, Lock, Unlock, Code, Wrench, X, Plus, Paperclip, Building2, Bold } from "lucide-react";
import RemoteButton from "../components/RemoteButton";
import SecureLinkModal from "../components/SecureLinkModal";
import { useAuth } from "../context/AuthContext";
import { StatusBadge, PriorityBadge, SLABadge, sx } from "../theme";
import useIsMobile from "../hooks/useIsMobile";
import { HtmlContent } from "../components/HtmlContent";

const API   = (process.env.REACT_APP_API_URL || "") + "/api";
const token = () => localStorage.getItem("midtc_token") || "";

// Local aliases — keep the rest of the file's `{ ...cardStyle }` spreads working
const inputStyle      = sx.input;
const cardInputStyle  = sx.cardInput;
const cardStyle       = sx.card;
const cardHeadStyle   = { ...sx.cardHead, borderRadius: "12px 12px 0 0" };

// ─── Send to Dev Modal ────────────────────────────────────────
function SendToDevModal({ ticket, agents, onClose, onSent }) {
  const [selectedDevs, setSelectedDevs] = useState([]);
  const [notes,        setNotes]        = useState("");
  const [priority,     setPriority]     = useState(ticket.priority);
  const [sending,      setSending]      = useState(false);
  const [error,        setError]        = useState("");

  const toggleDev = (id) => setSelectedDevs(p => p.includes(id) ? p.filter(d=>d!==id) : [...p, id]);

  const handleSend = async () => {
    if (!selectedDevs.length) { setError("Select at least one developer."); return; }
    setSending(true); setError("");
    try {
      const res  = await fetch(`${API}/tickets/${ticket.id}/send-to-dev`, {
        method:  "POST",
        headers: { "Content-Type":"application/json", Authorization:`Bearer ${token()}` },
        body:    JSON.stringify({ assigned_devs:selectedDevs, notes, priority }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send to dev queue");
      onSent(data);
    } catch (err) { setError(err.message); }
    finally { setSending(false); }
  };

  return (
    <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16 }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:"var(--t-panel)",border:"1px solid rgba(148,163,184,0.18)",borderRadius:16,width:"100%",maxWidth:520,boxShadow:"0 32px 80px rgba(0,0,0,0.6)" }}>
        <div style={{ padding:"18px 24px",borderBottom:"1px solid var(--t-border-soft)",display:"flex",alignItems:"center",justifyContent:"space-between" }}>
          <div>
            <div style={{ fontWeight:700,fontSize:15,color:"var(--t-text-primary)",display:"flex",alignItems:"center",gap:8 }}>
              <Code size={16} style={{ color:"#818cf8" }}/> Send to Dev Queue
            </div>
            <div style={{ fontSize:12,color:"var(--t-text-faint)",marginTop:2 }}>{ticket.ticket_number} — {ticket.subject}</div>
          </div>
          <button onClick={onClose} style={{ background:"none",border:"none",color:"var(--t-text-faint)",fontSize:20,cursor:"pointer" }}>×</button>
        </div>
        <div style={{ padding:24 }}>
          {error && <div style={{ background:"rgba(220,38,38,0.1)",border:"1px solid rgba(220,38,38,0.3)",borderRadius:8,padding:"10px 14px",color:"#f87171",fontSize:13,marginBottom:16 }}>⚠ {error}</div>}

          <div style={{ marginBottom:16 }}>
            <label style={{ fontSize:12,color:"var(--t-text-faint)",display:"block",marginBottom:8 }}>Assign to Developers *</label>
            <div style={{ display:"flex",flexDirection:"column",gap:6,maxHeight:200,overflowY:"auto" }}>
              {agents.map(a => (
                <div key={a.id}
                  onClick={() => toggleDev(a.id)}
                  style={{
                    display:"flex",alignItems:"center",gap:10,padding:"8px 12px",borderRadius:8,cursor:"pointer",
                    background: selectedDevs.includes(a.id) ? "rgba(129,140,248,0.1)" : "var(--t-input)",
                    border: `1px solid ${selectedDevs.includes(a.id) ? "rgba(129,140,248,0.4)" : "rgba(148,163,184,0.18)"}`,
                  }}>
                  <div style={{ width:18,height:18,borderRadius:4,border:`2px solid ${selectedDevs.includes(a.id)?"#818cf8":"#56677e"}`,background:selectedDevs.includes(a.id)?"#818cf8":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                    {selectedDevs.includes(a.id) && <span style={{ color:"#fff",fontSize:11,fontWeight:700 }}>✓</span>}
                  </div>
                  <div>
                    <div style={{ fontSize:13,fontWeight:600,color:"var(--t-text-primary)" }}>{a.first_name} {a.last_name}</div>
                    <div style={{ fontSize:11,color:"var(--t-text-faint)" }}>{a.role} · {a.email}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom:14 }}>
            <label style={{ fontSize:12,color:"var(--t-text-faint)",display:"block",marginBottom:5 }}>Priority</label>
            <select value={priority} onChange={e=>setPriority(e.target.value)}
              style={{ background:"var(--t-card-input)",border:"1px solid rgba(99,162,255,0.3)",borderRadius:7,padding:"8px 12px",color:"var(--t-text-primary)",fontSize:13,width:"100%",cursor:"pointer" }}>
              {["Critical","High","Medium","Low"].map(p=><option key={p}>{p}</option>)}
            </select>
          </div>

          <div style={{ marginBottom:4 }}>
            <label style={{ fontSize:12,color:"var(--t-text-faint)",display:"block",marginBottom:5 }}>Notes for Dev Team</label>
            <textarea value={notes} onChange={e=>setNotes(e.target.value)} rows={3} placeholder="Add any context or instructions for the dev team..."
              style={{ background:"var(--t-card-input)",border:"1px solid rgba(99,162,255,0.3)",borderRadius:7,padding:"8px 12px",color:"var(--t-text-primary)",fontSize:13,width:"100%",resize:"vertical",boxSizing:"border-box" }}/>
          </div>

          <div style={{ fontSize:12,color:"#818cf8",padding:"8px 12px",background:"rgba(129,140,248,0.08)",border:"1px solid rgba(129,140,248,0.2)",borderRadius:7,marginTop:12 }}>
            ✨ Claude AI will generate a professional briefing email and send it to the selected developers.
          </div>
        </div>
        <div style={{ padding:"16px 24px",borderTop:"1px solid var(--t-border-soft)",display:"flex",gap:10,justifyContent:"flex-end" }}>
          <button onClick={onClose} style={{ background:"transparent",color:"var(--t-text-muted)",border:"1px solid rgba(148,163,184,0.18)",padding:"9px 18px",borderRadius:8,fontWeight:600,fontSize:13,cursor:"pointer" }}>Cancel</button>
          <button onClick={handleSend} disabled={sending||!selectedDevs.length}
            style={{ background:"linear-gradient(135deg,#4f6fff,#7b61ff)",color:"#fff",border:"none",padding:"9px 18px",borderRadius:8,fontWeight:600,fontSize:13,cursor:"pointer",opacity:(!selectedDevs.length||sending)?0.5:1,boxShadow:"0 4px 16px rgba(99,102,241,0.4)" }}>
            {sending ? "Sending…" : "🔧 Send to Dev Queue"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Send to Infrastructure Modal ────────────────────────────
function SendToInfraModal({ ticket, agents, onClose, onSent }) {
  const [form, setForm] = useState({
    title:       ticket.subject,
    description: ticket.description || "",
    priority:    ticket.priority,
    due_date:    "",
    notes:       "",
  });
  const [assignedTo, setAssignedTo] = useState([]);
  const [sending,    setSending]    = useState(false);
  const [error,      setError]      = useState("");

  const toggleMember = (id) => setAssignedTo(p => p.includes(id) ? p.filter(m=>m!==id) : [...p, id]);
  const set = (k, v) => setForm(p => ({ ...p, [k]:v }));

  const handleSend = async () => {
    if (!form.title.trim()) { setError("Project title is required."); return; }
    if (!assignedTo.length) { setError("Assign at least one team member."); return; }
    setSending(true); setError("");
    try {
      const res  = await fetch(`${API}/tickets/${ticket.id}/send-to-infrastructure`, {
        method:  "POST",
        headers: { "Content-Type":"application/json", Authorization:`Bearer ${token()}` },
        body:    JSON.stringify({ ...form, assigned_to:assignedTo }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to create infrastructure project");
      onSent(data);
    } catch (err) { setError(err.message); }
    finally { setSending(false); }
  };

  const fieldStyle = { background:"var(--t-card-input)",border:"1px solid rgba(99,162,255,0.3)",borderRadius:7,padding:"8px 12px",color:"var(--t-text-primary)",fontSize:13,width:"100%",boxSizing:"border-box" };
  const labelStyle = { fontSize:12,color:"var(--t-text-faint)",display:"block",marginBottom:5 };

  return (
    <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.75)",zIndex:9999,display:"flex",alignItems:"center",justifyContent:"center",padding:16 }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{ background:"var(--t-panel)",border:"1px solid rgba(148,163,184,0.18)",borderRadius:16,width:"100%",maxWidth:560,maxHeight:"90vh",display:"flex",flexDirection:"column",boxShadow:"0 32px 80px rgba(0,0,0,0.6)" }}>
        <div style={{ padding:"18px 24px",borderBottom:"1px solid var(--t-border-soft)",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0 }}>
          <div>
            <div style={{ fontWeight:700,fontSize:15,color:"var(--t-text-primary)",display:"flex",alignItems:"center",gap:8 }}>
              <Wrench size={16} style={{ color:"#06b6d4" }}/> Create Infrastructure Project
            </div>
            <div style={{ fontSize:12,color:"var(--t-text-faint)",marginTop:2 }}>{ticket.ticket_number} — {ticket.subject}</div>
          </div>
          <button onClick={onClose} style={{ background:"none",border:"none",color:"var(--t-text-faint)",fontSize:20,cursor:"pointer" }}>×</button>
        </div>
        <div style={{ padding:24,overflowY:"auto",flex:1 }}>
          {error && <div style={{ background:"rgba(220,38,38,0.1)",border:"1px solid rgba(220,38,38,0.3)",borderRadius:8,padding:"10px 14px",color:"#f87171",fontSize:13,marginBottom:16 }}>⚠ {error}</div>}

          <div style={{ marginBottom:12 }}>
            <label style={labelStyle}>Project Title *</label>
            <input value={form.title} onChange={e=>set("title",e.target.value)} style={fieldStyle} placeholder="e.g. Network Infrastructure Upgrade"/>
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={labelStyle}>Description</label>
            <textarea value={form.description} onChange={e=>set("description",e.target.value)} rows={3}
              style={{ ...fieldStyle,resize:"vertical" }} placeholder="Describe the project scope and objectives..."/>
          </div>
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginBottom:12 }}>
            <div>
              <label style={labelStyle}>Priority</label>
              <select value={form.priority} onChange={e=>set("priority",e.target.value)} style={{ ...fieldStyle,cursor:"pointer" }}>
                {["Critical","High","Medium","Low"].map(p=><option key={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Due Date</label>
              <input type="date" value={form.due_date} onChange={e=>set("due_date",e.target.value)} style={fieldStyle}/>
            </div>
          </div>

          <div style={{ marginBottom:12 }}>
            <label style={labelStyle}>Assign Team Members *</label>
            <div style={{ display:"flex",flexDirection:"column",gap:6,maxHeight:180,overflowY:"auto" }}>
              {agents.map(a => (
                <div key={a.id}
                  onClick={() => toggleMember(a.id)}
                  style={{
                    display:"flex",alignItems:"center",gap:10,padding:"8px 12px",borderRadius:8,cursor:"pointer",
                    background: assignedTo.includes(a.id) ? "rgba(6,182,212,0.1)" : "var(--t-input)",
                    border: `1px solid ${assignedTo.includes(a.id) ? "rgba(6,182,212,0.4)" : "rgba(148,163,184,0.18)"}`,
                  }}>
                  <div style={{ width:18,height:18,borderRadius:4,border:`2px solid ${assignedTo.includes(a.id)?"#06b6d4":"#56677e"}`,background:assignedTo.includes(a.id)?"#06b6d4":"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0 }}>
                    {assignedTo.includes(a.id) && <span style={{ color:"#fff",fontSize:11,fontWeight:700 }}>✓</span>}
                  </div>
                  <div>
                    <div style={{ fontSize:13,fontWeight:600,color:"var(--t-text-primary)" }}>{a.first_name} {a.last_name}</div>
                    <div style={{ fontSize:11,color:"var(--t-text-faint)" }}>{a.role} · {a.email}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ marginBottom:4 }}>
            <label style={labelStyle}>Additional Notes</label>
            <textarea value={form.notes} onChange={e=>set("notes",e.target.value)} rows={2} placeholder="Any additional context..."
              style={{ ...fieldStyle,resize:"vertical" }}/>
          </div>

          <div style={{ fontSize:12,color:"#06b6d4",padding:"8px 12px",background:"rgba(6,182,212,0.08)",border:"1px solid rgba(6,182,212,0.2)",borderRadius:7,marginTop:12 }}>
            ✨ Claude AI will generate a professional project briefing email and send it to the assigned team.
          </div>
        </div>
        <div style={{ padding:"16px 24px",borderTop:"1px solid var(--t-border-soft)",display:"flex",gap:10,justifyContent:"flex-end",flexShrink:0 }}>
          <button onClick={onClose} style={{ background:"transparent",color:"var(--t-text-muted)",border:"1px solid rgba(148,163,184,0.18)",padding:"9px 18px",borderRadius:8,fontWeight:600,fontSize:13,cursor:"pointer" }}>Cancel</button>
          <button onClick={handleSend} disabled={sending||!form.title.trim()||!assignedTo.length}
            style={{ background:"linear-gradient(135deg,#0891b2,#06b6d4)",color:"#fff",border:"none",padding:"9px 18px",borderRadius:8,fontWeight:600,fontSize:13,cursor:"pointer",opacity:(!form.title.trim()||!assignedTo.length||sending)?0.5:1 }}>
            {sending ? "Creating…" : "🏗️ Create Project"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Success toast ────────────────────────────────────────────
function Toast({ message, color, onDismiss }) {
  useEffect(() => { const t = setTimeout(onDismiss, 4000); return () => clearTimeout(t); }, [onDismiss]);
  return (
    <div style={{ position:"fixed",bottom:24,right:24,background:color,color:"#fff",padding:"12px 20px",borderRadius:10,fontSize:13,fontWeight:600,boxShadow:"0 8px 32px rgba(0,0,0,0.4)",zIndex:9999,display:"flex",alignItems:"center",gap:10 }}>
      ✓ {message}
      <button onClick={onDismiss} style={{ background:"none",border:"none",color:"#fff",cursor:"pointer",fontSize:16,lineHeight:1 }}>×</button>
    </div>
  );
}

// ─── CC Panel ────────────────────────────────────────────────
function CcPanel({ ticketId, ccEmails, onUpdate }) {
  const [input,   setInput]   = useState("");
  const [saving,  setSaving]  = useState(false);
  const [error,   setError]   = useState("");
  const inputRef = useRef(null);

  const isValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());

  const add = async () => {
    const email = input.trim().toLowerCase();
    if (!email) return;
    if (!isValid(email)) { setError("Invalid email address."); return; }
    if (ccEmails.includes(email)) { setError("Already in CC."); return; }
    setSaving(true); setError("");
    try {
      await updateTicket(ticketId, { cc_emails: [...ccEmails, email] });
      onUpdate([...ccEmails, email]);
      setInput("");
    } catch { setError("Failed to save."); }
    finally { setSaving(false); }
  };

  const remove = async (email) => {
    const next = ccEmails.filter(e => e !== email);
    setSaving(true);
    try {
      await updateTicket(ticketId, { cc_emails: next });
      onUpdate(next);
    } catch { setError("Failed to save."); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ ...cardStyle }}>
      <div style={{ ...cardHeadStyle }}>CC Emails</div>
      <div style={{ padding:14 }}>
        {ccEmails.length === 0 && (
          <div style={{ fontSize:12, color:"var(--t-text-faint)", marginBottom:8 }}>No CC recipients.</div>
        )}
        {ccEmails.map(e => (
          <div key={e} style={{ display:"flex", alignItems:"center", justifyContent:"space-between",
            background:"var(--t-input-hi)", borderRadius:6, padding:"4px 8px", marginBottom:5, gap:6 }}>
            <span style={{ fontSize:12, color:"var(--t-text-body)", flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{e}</span>
            <button onClick={() => remove(e)} disabled={saving}
              style={{ background:"none", border:"none", color:"var(--t-text-faint)", cursor:"pointer", padding:0, display:"flex", alignItems:"center" }}>
              <X size={13}/>
            </button>
          </div>
        ))}
        {error && <div style={{ fontSize:11, color:"#f87171", marginBottom:6 }}>{error}</div>}
        <div style={{ display:"flex", gap:6, marginTop:4 }}>
          <input ref={inputRef} value={input} onChange={e => { setInput(e.target.value); setError(""); }}
            onKeyDown={e => e.key === "Enter" && add()}
            placeholder="email@example.com"
            style={{ flex:1, background:"var(--t-card-input)", border:"1px solid rgba(99,162,255,0.3)", borderRadius:6,
              padding:"5px 8px", color:"var(--t-text-primary)", fontSize:12, outline:"none" }}/>
          <button onClick={add} disabled={saving || !input.trim()}
            style={{
              background: input.trim() ? "linear-gradient(135deg,#4f6fff,#7b61ff)" : "var(--t-input)",
              border: input.trim() ? "none" : "1px solid rgba(148,163,184,0.18)",
              borderRadius:6,
              padding:"5px 8px", color: input.trim() ? "#fff" : "#56677e",
              cursor: input.trim() ? "pointer" : "not-allowed",
              display:"flex", alignItems:"center", gap:4, fontSize:12, fontWeight:600,
              boxShadow: input.trim() ? "0 4px 16px rgba(99,102,241,0.4)" : "none",
            }}>
            <Plus size={13}/> Add
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main TicketDetail ────────────────────────────────────────
export default function TicketDetail() {
  const { id }     = useParams();
  const navigate   = useNavigate();
  const { agent }  = useAuth();
  const isMobile   = useIsMobile();
  const [ticket,     setTicket]     = useState(null);
  const [agents,     setAgents]     = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [reply,      setReply]      = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [aiSuggest,  setAiSuggest]  = useState({ visible:false, loading:false, text:"" });
  const [sending,    setSending]    = useState(false);
  const [updating,   setUpdating]   = useState(false);
  const [showDev,      setShowDev]      = useState(false);
  const [showInfra,    setShowInfra]    = useState(false);
  const [toast,        setToast]        = useState(null);
  const [comments,        setComments]        = useState([]);
  const [newComment,      setNewComment]      = useState("");
  const [addingComment,   setAddingComment]   = useState(false);
  const [generatingSummary, setGeneratingSummary] = useState(false);
  const [ccEmails,          setCcEmails]          = useState([]);
  const [attachments,       setAttachments]       = useState([]);
  const [showSecureLink,    setShowSecureLink]    = useState(false);
  const fileInputRef = useRef(null);

  const canSendToQueue = ["admin","manager","technician"].includes(agent?.role);

  // Lock the parent <main> scroll so the inner thread pane can own scrolling
  useEffect(() => {
    const main = document.querySelector("main");
    if (!main) return;
    const prev = main.style.overflow;
    main.style.overflow = "hidden";
    return () => { main.style.overflow = prev; };
  }, []);

  useEffect(() => {
    Promise.all([getTicket(id), getAgents(), getTicketComments(id)])
      .then(([t, a, c]) => {
        setTicket(t.data);
        setCcEmails(t.data?.cc_emails || []);
        setAgents(a.data || []);
        setComments(c.data || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [id]);

  // Auto-refresh ticket + comments every 60 seconds.
  // Skip the refresh while the user is actively composing (reply box has text)
  // or while an update/send is in flight to avoid clobbering local state.
  useEffect(() => {
    const interval = setInterval(() => {
      if (sending || updating) return;
      Promise.all([getTicket(id), getTicketComments(id)])
        .then(([t, c]) => {
          setTicket(prev => {
            // Don't overwrite if user is mid-reply (reply state lives outside ticket)
            return t.data;
          });
          setComments(c.data || []);
        })
        .catch(() => {});
    }, 60000);
    return () => clearInterval(interval);
  }, [id, sending, updating]);

  const handleUpdate = async (field, value) => {
    setUpdating(true);
    try {
      const res = await updateTicket(id, { [field]: value });
      setTicket(prev => ({ ...prev, ...res.data }));
    } catch (e) { console.error(e); }
    finally { setUpdating(false); }
  };

  const handleSuggest = async (overrideContext = "") => {
    setAiSuggest({ visible:true, loading:true, text:"" });
    try {
      const res = await suggestResponse(id, overrideContext);
      const suggestion = res?.data?.suggestion;
      if (!suggestion) throw new Error("Empty response from server");
      setAiSuggest({ visible:true, loading:false, text:suggestion });
      setReply(suggestion);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.error || err?.message || "Unknown error";
      console.error("AI Suggest failed:", err?.response?.status, detail, err);
      setAiSuggest({ visible:true, loading:false, text:`Failed to generate suggestion: ${detail}` });
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    setAddingComment(true);
    try {
      const res = await addTicketComment(id, { body: newComment.trim() });
      setComments(prev => [...prev, res.data]);
      setNewComment("");
    } catch (e) { console.error(e); }
    finally { setAddingComment(false); }
  };

  const handleGenerateSummary = async () => {
    setGeneratingSummary(true);
    try {
      const token = localStorage.getItem("midtc_token");
      const res = await fetch(`${API}/tickets/${id}/generate-summary`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");
      setTicket(prev => ({ ...prev, ai_summary: data.summary }));
    } catch (e) {
      console.error(e);
    } finally {
      setGeneratingSummary(false);
    }
  };

  const agentSignature = (agent?.signature || "").replace(
    /(<img[^>]*?)style="[^"]*max-height:[^"]*"([^>]*>)/gi,
    '$1style="max-width:320px;width:100%;height:auto;display:block;margin-top:6px;"$2'
  );

  const handleSend = async () => {
    if (!reply.trim()) return;
    setSending(true);
    try {
      const fullBody = (!isInternal && agentSignature)
        ? `${reply}\n\n<hr style="border:none;border-top:1px solid #e2e8f0;margin:16px 0"><div style="font-size:14px;color:#374151;">${agentSignature}</div>`
        : reply;
      const fd = new FormData();
      fd.append("body",             fullBody);
      fd.append("is_internal",      String(isInternal));
      fd.append("is_ai_generated",  String(aiSuggest.visible && aiSuggest.text === reply));
      fd.append("ai_edited",        String(aiSuggest.visible && aiSuggest.text !== reply));
      attachments.forEach(f => fd.append("attachments", f));
      await addResponse(id, fd);
      const res = await getTicket(id);
      setTicket(res.data);
      setReply("");
      setAttachments([]);
      setAiSuggest({ visible:false, loading:false, text:"" });
    } catch (e) { console.error(e); }
    finally { setSending(false); }
  };

  const fmt      = (d) => new Date(d).toLocaleDateString("en-US", { month:"short", day:"numeric", year:"numeric", hour:"2-digit", minute:"2-digit" });
  const fmtShort = (d) => new Date(d).toLocaleDateString("en-US", { month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" });

  // Collapsible responses — all start expanded, click header to collapse
  const [expandedResponses, setExpandedResponses] = useState(() => new Set());

  useEffect(() => {
    if (ticket?.responses?.length) {
      // Expand only the newest reply (last in chronological array); collapse the rest
      setExpandedResponses(new Set([ticket.responses.length - 1]));
    }
  }, [ticket?.responses?.length]);

  const toggleResponse = (i) => {
    setExpandedResponses(prev => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  // Strip HTML and return a short preview string
  const previewText = (body) => {
    if (!body) return "";
    const stripped = body
      .replace(/\r\n/g, " ").replace(/\r/g, " ").replace(/\n/g, " ")
      .replace(/<[^>]*>/g, " ")
      .replace(/&nbsp;/g, " ").replace(/&lt;/g, "<").replace(/&gt;/g, ">")
      .replace(/&amp;/g, "&").replace(/&quot;/g, '"').replace(/&#39;/g, "'")
      .replace(/\s+/g, " ").trim();
    return stripped.length > 120 ? stripped.slice(0, 120) + "…" : stripped;
  };

  if (loading) return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:"100%", color:"var(--t-text-faint)" }}>
      <div style={{ width:28, height:28, border:"3px solid #6366f1", borderTopColor:"transparent", borderRadius:"50%" }} />
    </div>
  );

  if (!ticket) return (
    <div style={{ padding:40, textAlign:"center", color:"var(--t-text-faint)" }}>
      Ticket not found. <button onClick={() => navigate("/tickets")} style={{ color:"#818cf8", background:"none", border:"none", cursor:"pointer" }}>Go back</button>
    </div>
  );

  return (
    // Phones: stack conversation above the details rail and scroll the page as one;
    // desktop keeps the two independently-scrolling columns.
    <div style={{ display:"flex", flexDirection: isMobile ? "column" : "row", flex:1, minHeight:0, overflow: isMobile ? "auto" : "hidden" }}>
      <style>{`
        .email-html-content { color:var(--t-text-body); font-size:13px; line-height:1.7; word-break:break-word; }
        .email-html-content p { margin:4px 0; }
        .email-html-content a { color:#60a5fa; }
        .email-html-content img { max-width:100%; height:auto; border-radius:6px; border:1px solid rgba(148,163,184,0.18); display:block; margin:6px 0; }
        .email-html-content table { max-width:100%; border-collapse:collapse; }
        .email-html-content blockquote { border-left:3px solid rgba(148,163,184,0.18); margin:8px 0; padding-left:12px; color:var(--t-text-faint); }
        .email-html-content hr { border:none; border-top:1px solid rgba(148,163,184,0.11); margin:10px 0; }
        .email-html-content h1,.email-html-content h2,.email-html-content h3 { color:var(--t-text-primary); margin:10px 0 4px; }
        .email-html-content div { max-width:100%; }
        @keyframes spin { to { transform:rotate(360deg) } }
      `}</style>

      {/* Left — responses */}
      <div style={{ flex: isMobile ? "none" : 1, display:"flex", flexDirection:"column", overflow: isMobile ? "visible" : "hidden", minHeight:0, borderRight: isMobile ? "none" : "1px solid var(--t-border-soft)" }}>

        {/* Breadcrumb bar */}
        <div style={{ padding:"8px 20px", borderBottom:"1px solid rgba(148,163,184,0.08)", display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
          <button onClick={() => navigate("/tickets")} style={{ background:"none", border:"none", color:"var(--t-text-faint)", display:"flex", alignItems:"center", gap:4, fontSize:12, cursor:"pointer", padding:0 }}>
            <ArrowLeft size={12}/> Tickets
          </button>
          <span style={{ color:"var(--t-text-faint)", fontSize:12 }}>/</span>
          <span style={{ color:"#60a5fa", fontFamily:"monospace", fontSize:12, fontWeight:600 }}>{ticket.ticket_number}</span>
          <span style={{ color:"var(--t-text-faint)", fontSize:12 }}>/</span>
          <span style={{ color:"var(--t-text-faint)", fontSize:12, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:320 }}>{ticket.subject}</span>
          {updating && <div style={{ marginLeft:"auto", width:13, height:13, border:"2px solid #6366f1", borderTopColor:"transparent", borderRadius:"50%" }} />}
        </div>

        {/* Ticket head — subject + submitter chips + badge row */}
        <div style={{ padding:"18px 22px 14px", borderBottom:"1px solid var(--t-border-soft)", flexShrink:0, background:"var(--t-grad-panel-head)" }}>
          <div style={{ fontSize:18, fontWeight:800, color:"var(--t-text-primary)", letterSpacing:"-0.4px", lineHeight:1.35 }}>{ticket.subject}</div>

          {/* Submitter row */}
          <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:9, flexWrap:"wrap" }}>
            <div
              onClick={ticket.contact_id ? () => navigate(`/contacts/${ticket.contact_id}`) : undefined}
              title={ticket.contact_id ? "View contact profile" : undefined}
              style={{ display:"flex", alignItems:"center", gap:6, background:"var(--t-surface-mid)", border:"1px solid var(--t-border-soft)", borderRadius:99, padding:"3px 11px 3px 5px", cursor:ticket.contact_id?"pointer":"default", transition:"all 0.18s" }}
              onMouseEnter={e => { if (ticket.contact_id) { e.currentTarget.style.borderColor="rgba(79,111,255,0.6)"; e.currentTarget.style.background="rgba(79,111,255,0.12)"; } }}
              onMouseLeave={e => { e.currentTarget.style.borderColor="var(--t-border-soft)"; e.currentTarget.style.background="var(--t-surface-mid)"; }}>
              <div style={{ width:20, height:20, borderRadius:"50%", flexShrink:0, background:"linear-gradient(135deg,#1e3a5f,#4f6fff)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:8, fontWeight:700, color:"#fff" }}>
                {(ticket.submitter_name || ticket.client_name || "?")[0].toUpperCase()}
              </div>
              <span style={{ fontSize:12, fontWeight:600, color:"var(--t-text-primary)" }}>{ticket.submitter_name || ticket.client_name || "—"}</span>
            </div>
            {ticket.client_name && (
              <>
                <span style={{ color:"var(--t-text-faint)" }}>·</span>
                <div
                  onClick={ticket.client_id ? () => navigate(`/clients/${ticket.client_id}`) : undefined}
                  title={ticket.client_id ? "View client profile" : undefined}
                  style={{ display:"flex", alignItems:"center", gap:5, background:"var(--t-surface-mid)", border:"1px solid var(--t-border-soft)", borderRadius:99, padding:"3px 11px", fontSize:12, color:"var(--t-text-muted)", cursor:ticket.client_id?"pointer":"default", transition:"all 0.18s" }}
                  onMouseEnter={e => { if (ticket.client_id) { e.currentTarget.style.borderColor="rgba(79,111,255,0.6)"; e.currentTarget.style.color="var(--accent-blue, #5b8cff)"; } }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor="var(--t-border-soft)"; e.currentTarget.style.color="var(--t-text-muted)"; }}>
                  <Building2 size={12}/>
                  {ticket.client_name}
                </div>
              </>
            )}
            {ticket.submitter_email && (
              <span style={{ fontSize:12, color:"var(--t-text-muted)" }}>{ticket.submitter_email}</span>
            )}
            {ccEmails.length > 0 && (
              <>
                <span style={{ color:"var(--t-text-faint)", fontSize:11 }}>CC:</span>
                {ccEmails.slice(0, 3).map(e => (
                  <div key={e} style={{ display:"inline-flex", alignItems:"center", gap:4,
                    background:"rgba(99,102,241,0.1)", border:"1px solid rgba(99,102,241,0.22)",
                    borderRadius:99, padding:"2px 9px", fontSize:11, color:"#a5b4fc", whiteSpace:"nowrap" }}>
                    {e}
                  </div>
                ))}
                {ccEmails.length > 3 && (
                  <div style={{ display:"inline-flex", alignItems:"center",
                    background:"rgba(99,102,241,0.08)", border:"1px solid rgba(99,102,241,0.18)",
                    borderRadius:99, padding:"2px 9px", fontSize:11, color:"#818cf8" }}>
                    +{ccEmails.length - 3} more
                  </div>
                )}
              </>
            )}
          </div>

          {/* Badge row */}
          <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:10, flexWrap:"wrap" }}>
            <StatusBadge status={ticket.status} />
            <PriorityBadge priority={ticket.priority} />
            <SLABadge status={ticket.sla_status} />
            <div style={{ width:3, height:3, borderRadius:"50%", background:"var(--t-text-faint)", flexShrink:0 }} />
            <span style={{ fontSize:12, color:"var(--t-text-muted)" }}>Opened {fmtShort(ticket.created_at)}</span>
          </div>
        </div>

        {/* Scrollable thread — newest reply first, original request pinned at bottom */}
        <div style={{ flex: isMobile ? "none" : 1, minHeight:0, overflowY: isMobile ? "visible" : "auto", padding: isMobile ? "14px 14px" : "16px 20px", display:"flex", flexDirection:"column", gap:12 }}>

          {/* Responses */}
          {ticket.responses?.length === 0 && (
            <div style={{ textAlign:"center", color:"var(--t-text-faint)", fontSize:13, padding:32 }}>No responses yet.</div>
          )}
          {ticket.responses?.map((r, i) => ({ r, i })).reverse().map(({ r, i }) => {
            const isAgent     = !!r.agent_id;
            const displayName = r.agent_name || r.contact_name || r.sender_name || "Client";
            const isExpanded  = expandedResponses.has(i);
            const preview     = previewText(r.body);

            const bgColor     = r.is_internal ? "rgba(120,53,15,0.12)" : isAgent ? "var(--t-panel)" : "var(--t-page)";
            const borderColor = r.is_internal ? "#92400e" : isAgent ? "rgba(99,162,255,0.2)" : "var(--t-border-soft)";
            const avatarBg    = r.is_internal ? "#78350f" : isAgent ? "var(--t-card-head)" : "var(--t-input)";
            const avatarColor = r.is_internal ? "#fcd34d" : isAgent ? "#93c5fd" : "var(--t-text-muted)";
            const nameColor   = r.is_internal ? "#fcd34d" : isAgent ? "#93c5fd" : "var(--t-text-primary)";

            return (
              <div key={i} style={{ background:bgColor, border:`1px solid ${borderColor}`, borderRadius:12, overflow:"hidden", flexShrink:0 }}>

                {/* Clickable header — always visible */}
                <div onClick={() => toggleResponse(i)} style={{
                  padding:"12px 16px",
                  borderBottom: isExpanded ? `1px solid ${borderColor}` : "none",
                  display:"flex", alignItems:"center", gap:10,
                  cursor:"pointer", userSelect:"none",
                  background: isExpanded ? "transparent" : "rgba(255,255,255,0.02)",
                }}>
                  {/* Avatar */}
                  <div style={{ width:32, height:32, borderRadius:"50%", flexShrink:0, background:avatarBg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:700, color:avatarColor }}>
                    {displayName[0]?.toUpperCase() || "?"}
                  </div>

                  {/* Name + badges */}
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                      <span style={{ fontSize:13, fontWeight:700, color:nameColor }}>{displayName}</span>
                      {r.is_internal && <span style={{ fontSize:10, background:"#78350f", color:"var(--badge-amber)", padding:"2px 8px", borderRadius:20, fontWeight:700 }}>INTERNAL</span>}
                      {r.is_ai_generated && <span style={{ fontSize:10, background:"rgba(99,102,241,0.15)", color:"var(--badge-violet)", padding:"2px 8px", borderRadius:20 }}>AI</span>}
                      {r.source === "import" && <span style={{ fontSize:10, background:"rgba(148,163,184,0.1)", color:"var(--t-text-faint)", padding:"2px 8px", borderRadius:20 }}>IMPORTED</span>}
                    </div>
                    {/* Preview line — only when collapsed */}
                    {!isExpanded && (
                      <div style={{ fontSize:12, color:"var(--t-text-faint)", marginTop:3, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {preview || <em style={{ color:"var(--t-text-faint)" }}>Click to expand</em>}
                      </div>
                    )}
                  </div>

                  {/* Date + chevron */}
                  <div style={{ display:"flex", alignItems:"center", gap:8, flexShrink:0 }}>
                    <span style={{ fontSize:11, color:"var(--t-text-faint)" }}>{fmtShort(r.created_at)}</span>
                    <span style={{ color:"var(--t-text-faint)", fontSize:12, transition:"transform 0.15s", display:"inline-block", transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)" }}>▼</span>
                  </div>
                </div>

                {/* Expanded body */}
                {isExpanded && (
                  <div style={{ padding:"16px 18px" }}>
                    <HtmlContent text={r.body} />
                    {r.attachments?.length > 0 && (
                      <div style={{ marginTop:10, paddingTop:10, borderTop:"1px solid rgba(148,163,184,0.1)" }}>
                        {r.attachments.map((a, ai) => {
                          const isImg = a.mimetype?.startsWith("image/");
                          return isImg ? (
                            <img key={ai} src={`${process.env.REACT_APP_API_URL}${a.url}`} alt={a.name}
                              style={{ maxWidth:"100%", maxHeight:400, borderRadius:8, border:"1px solid rgba(148,163,184,0.18)", display:"block", marginBottom:8 }}
                              onError={e => { e.target.style.display="none"; }} />
                          ) : (
                            <a key={ai} href={`${process.env.REACT_APP_API_URL}${a.url}`} target="_blank" rel="noreferrer"
                              style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 9px", marginRight:6, marginBottom:6,
                                background:"rgba(99,162,255,0.08)", border:"1px solid rgba(99,162,255,0.2)",
                                borderRadius:6, fontSize:11, color:"var(--badge-blue)", textDecoration:"none" }}>
                              <Paperclip size={10}/>{a.name}
                            </a>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Original request card — oldest message, pinned at bottom, always expanded */}
          <div style={{ background:"var(--t-surface-deep)", border:"1px solid rgba(99,162,255,0.2)", borderRadius:12, overflow:"hidden", flexShrink:0 }}>
            <div style={{ padding:"12px 16px", borderBottom:"1px solid rgba(99,162,255,0.15)", display:"flex", justifyContent:"space-between", alignItems:"center", gap:8 }}>
              <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:0 }}>
                <div style={{ width:32, height:32, borderRadius:"50%", background:"linear-gradient(135deg,#0d1f3c,#1a3254)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:700, color:"#60a5fa", flexShrink:0 }}>
                  {(ticket.submitter_name || ticket.client_name || "?")[0].toUpperCase()}
                </div>
                <div style={{ minWidth:0 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:"var(--badge-blue)" }}>{ticket.submitter_name || ticket.client_name || "Client"}</div>
                  {ticket.submitter_email && <div style={{ fontSize:11, color:"var(--t-text-faint)", marginTop:1 }}>{ticket.submitter_email}</div>}
                </div>
                <span style={{ fontSize:10, background:"rgba(99,162,255,0.1)", color:"#60a5fa", padding:"3px 10px", borderRadius:20, fontWeight:700, border:"1px solid rgba(99,162,255,0.25)", flexShrink:0 }}>ORIGINAL REQUEST</span>
              </div>
              <span style={{ fontSize:11, color:"var(--t-text-faint)", flexShrink:0 }}>{ticket.created_at ? fmtShort(ticket.created_at) : ""}</span>
            </div>
            <div style={{ padding:"16px 18px" }}>
              {ticket.description
                ? <HtmlContent text={ticket.description} />
                : <span style={{ color:"var(--t-text-faint)", fontSize:13, fontStyle:"italic" }}>No description — see replies below.</span>
              }
              {ticket.attachments?.length > 0 && (
                <div style={{ marginTop:10, paddingTop:10, borderTop:"1px solid rgba(148,163,184,0.1)", display:"flex", flexWrap:"wrap", gap:6 }}>
                  {ticket.attachments.map((a, ai) => (
                    <a key={ai} href={`${process.env.REACT_APP_API_URL}${a.url}`} target="_blank" rel="noreferrer"
                      style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 9px",
                        background:"rgba(99,162,255,0.08)", border:"1px solid rgba(99,162,255,0.2)",
                        borderRadius:6, fontSize:11, color:"var(--badge-blue)", textDecoration:"none" }}>
                      <Paperclip size={10}/>{a.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Compose area */}
        <div style={{ flexShrink:0, borderTop:"1px solid var(--t-border-soft)", padding:"13px 22px", background:"var(--t-compose-bg)" }}>

          {/* AI suggestion panel */}
          {aiSuggest.visible && (
            <div style={{ background:"var(--t-card-head)", border:"1px solid rgba(99,162,255,0.2)", borderRadius:8, padding:10, marginBottom:10 }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
                <Sparkles size={13} style={{ color:"#818cf8" }}/>
                <span style={{ fontSize:11, color:"#818cf8", fontWeight:600 }}>Claude Suggested Response</span>
                <button onClick={() => setAiSuggest({ visible:false, loading:false, text:"" })} style={{ marginLeft:"auto", background:"none", border:"none", color:"var(--t-text-faint)", fontSize:16, lineHeight:1, cursor:"pointer" }}>×</button>
              </div>
              {aiSuggest.loading
                ? <div style={{ color:"var(--t-text-faint)", fontSize:12 }}>Generating response...</div>
                : <div style={{ color:"var(--t-text-muted)", fontSize:12, lineHeight:1.6, maxHeight:120, overflowY:"auto" }}>{aiSuggest.text}</div>
              }
            </div>
          )}

          {/* Tabs */}
          <div style={{ display:"flex", gap:2, marginBottom:10 }}>
            {[["Reply to Client", false], ["Internal Note", true]].map(([label, internal]) => (
              <button key={label} onClick={() => setIsInternal(internal)} style={{
                padding:"5px 12px", borderRadius:6, fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit",
                background: isInternal === internal
                  ? (internal ? "rgba(251,191,36,0.12)" : "linear-gradient(135deg,rgba(99,102,241,0.22),rgba(99,102,241,0.08))")
                  : "none",
                color: isInternal === internal ? (internal ? "#fcd34d" : "#818cf8") : "var(--t-text-faint)",
                border: isInternal === internal
                  ? (internal ? "1px solid rgba(251,191,36,0.25)" : "1px solid rgba(99,102,241,0.28)")
                  : "1px solid transparent",
              }}>{label}</button>
            ))}
          </div>

          {/* Textarea */}
          <textarea value={reply} onChange={e => setReply(e.target.value)}
            placeholder={isInternal ? "Add an internal note… (Ctrl+Enter to send)" : "Type your reply… (Ctrl+Enter to send)"}
            onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleSend(); }}
            rows={4}
            style={{ width:"100%", boxSizing:"border-box", resize:"vertical",
              background:isInternal?"var(--t-note-bg)":"var(--t-input)",
              border:"1px solid", borderColor:isInternal?"var(--t-note-bdr)":"var(--t-border-mid)",
              borderRadius:8, padding:"11px 13px", color:"var(--t-text-primary)", fontSize:13,
              lineHeight:1.6, fontFamily:"inherit", outline:"none",
              borderBottomLeftRadius: (!isInternal && agentSignature) ? 0 : 8,
              borderBottomRightRadius: (!isInternal && agentSignature) ? 0 : 8,
            }}
          />

          {/* Signature preview */}
          {!isInternal && agentSignature && (
            <div style={{ background:"var(--t-panel)", border:"1px solid rgba(148,163,184,0.18)", borderTop:"1px dashed rgba(99,162,255,0.2)",
              borderBottomLeftRadius:8, borderBottomRightRadius:8, padding:"8px 12px" }}>
              <div style={{ fontSize:10, fontWeight:600, color:"var(--t-text-faint)", textTransform:"uppercase", letterSpacing:"0.6px", marginBottom:4 }}>Signature</div>
              <div style={{ fontSize:14, color:"var(--t-text-body)", lineHeight:1.6 }} dangerouslySetInnerHTML={{ __html: agentSignature }} />
            </div>
          )}

          {/* Attached file chips */}
          {attachments.length > 0 && (
            <div style={{ display:"flex", flexWrap:"wrap", gap:5, marginTop:8 }}>
              {attachments.map((f, i) => (
                <span key={i} style={{ display:"inline-flex", alignItems:"center", gap:5, padding:"3px 8px",
                  background:"rgba(99,162,255,0.08)", border:"1px solid rgba(99,162,255,0.2)", borderRadius:6,
                  fontSize:11, color:"var(--badge-blue)" }}>
                  <Paperclip size={10}/>{f.name}
                  <button onClick={() => setAttachments(prev => prev.filter((_, j) => j !== i))}
                    style={{ background:"none", border:"none", color:"var(--t-text-faint)", cursor:"pointer", padding:0, display:"flex", alignItems:"center" }}>
                    <X size={11}/>
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Footer: tools left, actions right */}
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginTop:10 }}>
            <div style={{ display:"flex", gap:6 }}>
              <input ref={fileInputRef} type="file" multiple style={{ display:"none" }}
                onChange={e => setAttachments(prev => [...prev, ...Array.from(e.target.files)])}
              />
              {[
                { title:"Bold", icon:<span style={{ fontSize:12, fontWeight:700, fontFamily:"serif" }}>B</span>, onClick: undefined },
                { title:"Attach", icon:<Paperclip size={12}/>, onClick: () => fileInputRef.current?.click(), badge: attachments.length || null },
                { title:"Code block", icon:<span style={{ fontSize:11, fontFamily:"monospace" }}>&lt;/&gt;</span>, onClick: undefined },
              ].map(({ title, icon, onClick, badge }) => (
                <button key={title} title={title} onClick={onClick}
                  style={{ width:30, height:30, borderRadius:6, background: badge ? "rgba(99,162,255,0.1)" : "none",
                    border:"1px solid", borderColor: badge ? "rgba(99,162,255,0.3)" : "rgba(148,163,184,0.18)",
                    color: badge ? "#93c5fd" : "var(--t-text-faint)", cursor:"pointer",
                    display:"flex", alignItems:"center", justifyContent:"center", gap:2, position:"relative" }}>
                  {icon}
                  {badge > 0 && <span style={{ position:"absolute", top:-5, right:-5, background:"#4f6fff", color:"#fff", borderRadius:99, fontSize:9, fontWeight:700, padding:"1px 4px", lineHeight:1 }}>{badge}</span>}
                </button>
              ))}
            </div>

            <div style={{ display:"flex", gap:8, alignItems:"center" }}>
              <button onClick={() => handleSuggest()} disabled={aiSuggest.loading} style={{
                display:"flex", alignItems:"center", gap:6,
                padding:"7px 14px", borderRadius:6, fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit",
                background:"linear-gradient(135deg,rgba(168,85,247,0.2),rgba(168,85,247,0.08))",
                border:"1px solid rgba(168,85,247,0.3)", color:"#c084fc",
                opacity:aiSuggest.loading?0.6:1,
              }}>
                <Sparkles size={13}/> AI Suggest
              </button>
              <button onClick={handleSend} disabled={sending || !reply.trim()} style={{
                display:"flex", alignItems:"center", gap:7,
                padding:"8px 18px", borderRadius:6, fontSize:13, fontWeight:700, cursor:reply.trim()?"pointer":"not-allowed", fontFamily:"inherit",
                background: reply.trim() ? "linear-gradient(135deg,#4f6fff,#7b61ff)" : "var(--t-input)",
                color: reply.trim() ? "#fff" : "#56677e",
                border: reply.trim() ? "none" : "1px solid rgba(148,163,184,0.18)",
                boxShadow: reply.trim() ? "0 4px 16px rgba(99,102,241,0.4)" : "none",
                opacity:sending?0.7:1,
              }}>
                <Send size={13}/> {sending ? "Sending..." : "Send Reply"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Right — ticket info panel */}
      <div style={{ width: isMobile ? "100%" : 300, flexShrink:0, minHeight:0, overflowY: isMobile ? "visible" : "auto", padding:"14px 13px", display:"flex", flexDirection:"column", gap:11, background:"var(--t-page)", borderTop: isMobile ? "1px solid var(--t-border-soft)" : "none" }}>

        {/* Remote Session buttons — Main (RustDesk) + Backup (MeshCentral) */}
        <RemoteButton
          mode="rustdesk"
          deviceId={ticket.device_id || ""}
          deviceName={ticket.device_name || ticket.device_id || "No device linked"}
          contactName={ticket.submitter_name || ticket.client_name}
          rustdeskId={ticket.rustdesk_id}
          fullWidth
        />
        <RemoteButton
          mode="mesh"
          deviceId={ticket.device_id || ""}
          deviceName={ticket.device_name || ticket.device_id || "No device linked"}
          contactName={ticket.submitter_name || ticket.client_name}
          meshcentralNodeId={ticket.meshcentral_node_id}
          fullWidth
        />

        {/* Secure Data Link */}
        <button onClick={() => setShowSecureLink(true)} style={{
          width:"100%", display:"flex", alignItems:"center", justifyContent:"center", gap:7,
          padding:"9px 8px", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer",
          background:"rgba(29,78,216,0.14)", border:"1px solid rgba(59,130,246,0.3)", color:"#93c5fd",
          boxShadow:"0 0 8px rgba(29,78,216,0.2)",
        }}>
          <Lock size={13}/> Send Secure Link
        </button>

        {/* Queue actions — dev + infra row */}
        {canSendToQueue && (
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={() => setShowDev(true)} style={{
              flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:6,
              padding:"9px 8px", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer",
              background:"rgba(124,58,237,0.18)", border:"1px solid rgba(124,58,237,0.35)", color:"#a78bfa",
              boxShadow:"0 0 8px rgba(124,58,237,0.2)",
            }}>
              <Code size={13}/> Send to Dev
            </button>
            <button onClick={() => setShowInfra(true)} style={{
              flex:1, display:"flex", alignItems:"center", justifyContent:"center", gap:6,
              padding:"9px 8px", borderRadius:8, fontSize:12, fontWeight:700, cursor:"pointer",
              background:"rgba(249,115,22,0.15)", border:"1px solid rgba(249,115,22,0.3)", color:"#fb923c",
            }}>
              <Wrench size={13}/> Infrastructure
            </button>
          </div>
        )}

        {/* Status & Priority card */}
        <div style={{ ...cardStyle }}>
          <div style={{ ...cardHeadStyle }}>Status &amp; Priority</div>
          <div style={{ padding:15, display:"flex", flexDirection:"column", gap:14 }}>
            {[
              ["STATUS",   "status",   ["Open","In Progress","On Hold","Resolved","Closed"]],
              ["PRIORITY", "priority", ["Critical","High","Medium","Low"]],
              ["CATEGORY", "category", ["General","Email","Network","Hardware","Software","Security","Onboarding","Performance","Other"]],
            ].map(([label, field, opts]) => (
              <div key={field}>
                <label style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", color:"#7a92aa", display:"block", marginBottom:6 }}>{label}</label>
                <select value={ticket[field]} onChange={e => handleUpdate(field, e.target.value)}
                  style={{ ...cardInputStyle, fontSize:13, padding:"9px 12px", cursor:"pointer" }}>
                  {opts.map(o => <option key={o}>{o}</option>)}
                </select>
              </div>
            ))}
            <div>
              <label style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", color:"#7a92aa", display:"block", marginBottom:6 }}>SLA</label>
              <select value={ticket.sla_status || "ok"} onChange={e => handleUpdate("sla_status", e.target.value)}
                style={{ ...cardInputStyle, fontSize:13, padding:"9px 12px", cursor:"pointer" }}>
                <option value="ok">In SLA</option>
                <option value="hold">On Hold</option>
                <option value="breach">Breach</option>
              </select>
            </div>
          </div>
        </div>

        {/* SLA Timer card */}
        <div style={{ ...cardStyle }}>
          <div style={{ ...cardHeadStyle }}>SLA Timer</div>
          <div style={{ padding:15 }}>
            {(() => {
              const elapsedMs = ticket.created_at ? Date.now() - new Date(ticket.created_at) : 0;
              const elapsedH  = Math.floor(elapsedMs / 3600000);
              const elapsedM  = Math.floor((elapsedMs % 3600000) / 60000);
              const due       = ticket.sla_due_at ? new Date(ticket.sla_due_at) : null;
              const totalMs   = due && ticket.created_at ? due - new Date(ticket.created_at) : null;
              const pct       = totalMs ? Math.min(100, Math.round(elapsedMs / totalMs * 100)) : 50;
              const isBreach  = ticket.sla_status === "breach";
              const isHold    = ticket.sla_status === "hold";
              const fillBg    = isBreach ? "linear-gradient(90deg,#f87171,#ef4444)" : isHold ? "linear-gradient(90deg,#fbbf24,#f59e0b)" : "linear-gradient(90deg,#34d399,#10b981)";
              const fillGlow  = isBreach ? "0 0 12px rgba(239,68,68,0.55)" : isHold ? "0 0 10px rgba(251,191,36,0.5)" : "0 0 10px rgba(52,211,153,0.5)";
              return (
                <>
                  <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", color:"#7a92aa", marginBottom:10 }}>
                    {due ? `Due: ${fmt(ticket.sla_due_at)}` : "No SLA deadline set"}
                  </div>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                    <span style={{ fontSize:12, color:"var(--t-text-muted)" }}>Elapsed: {elapsedH}h {elapsedM}m</span>
                    <span style={{ fontSize:11, fontWeight:700, color: isBreach ? "#f87171" : isHold ? "#fbbf24" : "#34d399" }}>
                      {isBreach ? "BREACH" : isHold ? "ON HOLD" : "IN SLA"}
                    </span>
                  </div>
                  <div style={{ height:9, background:"rgba(255,255,255,0.12)", borderRadius:99, overflow:"hidden" }}>
                    <div style={{ height:"100%", width:`${pct}%`, borderRadius:99, background:fillBg, boxShadow:fillGlow }} />
                  </div>
                  {isBreach && <div style={{ fontSize:11, color:"#f87171", marginTop:7, fontWeight:500 }}>SLA target exceeded</div>}
                </>
              );
            })()}
          </div>
        </div>

        {/* Assigned To card */}
        <div style={{ ...cardStyle }}>
          <div style={{ ...cardHeadStyle }}>Assigned To</div>
          <div style={{ padding:15 }}>
            {(() => {
              const assignedAgent = agents.find(a => a.id === ticket.assigned_to);
              return assignedAgent ? (
                <div style={{ display:"flex", alignItems:"center", gap:9, marginBottom:12 }}>
                  <div style={{ width:30, height:30, borderRadius:"50%", flexShrink:0,
                    background:"linear-gradient(135deg,#312e81,#4f6fff)", color:"var(--badge-violet)",
                    display:"flex", alignItems:"center", justifyContent:"center",
                    fontSize:11, fontWeight:700, boxShadow:"0 0 10px rgba(99,102,241,0.3)" }}>
                    {assignedAgent.first_name.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontSize:13, fontWeight:700, color:"var(--t-text-primary)" }}>{assignedAgent.first_name} {assignedAgent.last_name}</div>
                    <div style={{ fontSize:11, color:"var(--t-text-faint)" }}>Assigned Agent</div>
                  </div>
                </div>
              ) : null;
            })()}
            <select value={ticket.assigned_to || ""} onChange={e => handleUpdate("assigned_to", e.target.value)}
              style={{ ...cardInputStyle, fontSize:13, padding:"9px 12px", cursor:"pointer" }}>
              <option value="">Unassigned</option>
              {agents.map(a => <option key={a.id} value={a.id}>{a.first_name} {a.last_name}</option>)}
            </select>
          </div>
        </div>

        {/* Client card */}
        <div style={{ ...cardStyle }}>
          <div style={{ ...cardHeadStyle }}>Client</div>
          <div style={{ padding:15, display:"flex", flexDirection:"column", gap:14 }}>
            {ticket.client_name && (
              <div>
                <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", color:"#7a92aa", marginBottom:6 }}>Company</div>
                {ticket.client_id
                  ? <div onClick={() => navigate(`/clients/${ticket.client_id}`)} style={{ fontSize:13, fontWeight:600, color:"var(--accent-blue, #5b8cff)", cursor:"pointer", display:"inline-flex", alignItems:"center", gap:4 }} title="View client profile">{ticket.client_name} <span style={{ fontSize:11 }}>→</span></div>
                  : <div style={{ fontSize:13, fontWeight:500, color:"var(--t-text-primary)" }}>{ticket.client_name}</div>}
              </div>
            )}
            {ticket.submitter_name && (
              <div>
                <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", color:"#7a92aa", marginBottom:6 }}>Submitter</div>
                {ticket.contact_id
                  ? <div onClick={() => navigate(`/contacts/${ticket.contact_id}`)} style={{ fontSize:13, fontWeight:600, color:"var(--accent-blue, #5b8cff)", cursor:"pointer", display:"inline-flex", alignItems:"center", gap:4 }} title="View contact profile">{ticket.submitter_name} <span style={{ fontSize:11 }}>→</span></div>
                  : <div style={{ fontSize:13, fontWeight:500, color:"var(--t-text-primary)" }}>{ticket.submitter_name}</div>}
                {ticket.submitter_email && <div style={{ fontSize:11, color:"var(--t-text-faint)", marginTop:2 }}>{ticket.submitter_email}</div>}
              </div>
            )}
            {ticket.submitter_phone && (
              <div>
                <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", color:"#7a92aa", marginBottom:6 }}>Phone</div>
                <div style={{ fontSize:12, color:"var(--t-text-body)" }}>{ticket.submitter_phone}</div>
              </div>
            )}
            {ticket.client_tier && (
              <div>
                <div style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.07em", color:"#7a92aa", marginBottom:6 }}>Client Tier</div>
                <span style={{ display:"inline-flex", padding:"3px 10px", borderRadius:99, fontSize:10, fontWeight:700,
                  background:"rgba(99,102,241,0.16)", color:"#818cf8", border:"1px solid rgba(99,102,241,0.3)" }}>
                  {ticket.client_tier}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Activity card */}
        <div style={{ ...cardStyle }}>
          <div style={{ ...cardHeadStyle }}>Activity</div>
          <div style={{ padding:15, display:"flex", flexDirection:"column", gap:10 }}>
            {[
              { label:"Ticket created",  val:ticket.created_at,        dotColor:"#56677e",  dotGlow:null },
              { label:"First response",  val:ticket.first_response_at, dotColor:"#34d399",  dotGlow:"rgba(52,211,153,0.7)" },
              { label:"Last activity",   val:ticket.last_activity_at,  dotColor:"#6366f1",  dotGlow:"rgba(99,102,241,0.7)" },
              { label:"SLA due",         val:ticket.sla_due_at,        dotColor:ticket.sla_status==="breach"?"#ef4444":"#fbbf24", dotGlow:ticket.sla_status==="breach"?"rgba(239,68,68,0.7)":"rgba(251,191,36,0.7)" },
              { label:"Resolved",        val:ticket.resolved_at,       dotColor:"#34d399",  dotGlow:"rgba(52,211,153,0.7)" },
            ].filter(x => x.val).map(({ label, val, dotColor, dotGlow }) => (
              <div key={label} style={{ display:"flex", gap:10 }}>
                <div style={{ width:8, height:8, borderRadius:"50%", marginTop:4, flexShrink:0,
                  background:dotColor, boxShadow: dotGlow ? `0 0 7px ${dotGlow}` : "none" }} />
                <div>
                  <div style={{ fontSize:12, color:"var(--t-text-muted)", lineHeight:1.4 }}>{label}</div>
                  <div style={{ fontSize:10, color:"var(--t-text-faint)", marginTop:2 }}>{fmt(val)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CC emails */}
        <CcPanel ticketId={id} ccEmails={ccEmails} onUpdate={setCcEmails} />

        {/* Internal notes */}
        <div style={{ ...cardStyle }}>
          <div style={{ ...cardHeadStyle }}>Internal Notes</div>
          <div style={{ padding:15 }}>
            <textarea defaultValue={ticket.internal_notes || ""} onBlur={e => handleUpdate("internal_notes", e.target.value)}
              placeholder="Add private notes..." rows={4}
              style={{ ...cardInputStyle, fontSize:12, resize:"vertical" }}/>
          </div>
        </div>

        {/* AI Resolution Summary */}
        {(ticket.status === "Resolved" || ticket.status === "Closed") && (
          <div style={{ ...cardStyle }}>
            <div style={{ ...cardHeadStyle, display:"flex", alignItems:"center", justifyContent:"space-between", borderRadius:"12px 12px 0 0" }}>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <Sparkles size={11} color="#818cf8" />
                <span>AI Resolution Summary</span>
              </div>
              <button
                onClick={handleGenerateSummary}
                disabled={generatingSummary}
                style={{ fontSize:11, padding:"2px 8px", borderRadius:6, border:"1px solid rgba(99,102,241,0.3)",
                  background:"rgba(99,102,241,0.12)",
                  color: generatingSummary ? "#6366f1" : "#818cf8",
                  cursor: generatingSummary ? "not-allowed" : "pointer", fontWeight:600 }}
              >
                {generatingSummary ? "Generating…" : ticket.ai_summary ? "Regenerate" : "Generate"}
              </button>
            </div>
            <div style={{ padding:14 }}>
              {ticket.ai_summary ? (
                <div style={{ fontSize:12, color:"var(--t-text-body)", lineHeight:1.7, whiteSpace:"pre-wrap" }}>{ticket.ai_summary}</div>
              ) : (
                <div style={{ fontSize:12, color:"var(--t-text-faint)", fontStyle:"italic" }}>
                  No summary yet. {ticket.status === "Resolved" ? "Will be auto-generated when this ticket closes, or click Generate." : "Click Generate to create one."}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Comments */}
        <div style={{ ...cardStyle }}>
          <div style={{ ...cardHeadStyle, display:"flex", alignItems:"center", justifyContent:"space-between", borderRadius:"12px 12px 0 0" }}>
            <span>Comments</span>
            {comments.length > 0 && (
              <span style={{ background:"rgba(148,163,184,0.12)", color:"var(--t-text-muted)", borderRadius:10, padding:"1px 7px", fontSize:10 }}>{comments.length}</span>
            )}
          </div>
          <div style={{ padding:14 }}>
            {comments.length === 0 && (
              <div style={{ fontSize:12, color:"var(--t-text-faint)", marginBottom:10 }}>No comments yet.</div>
            )}
            {comments.length > 0 && (
              <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:10, maxHeight:300, overflowY:"auto" }}>
                {comments.map((c, i) => (
                  <div key={c.id || i} style={{ paddingBottom:8, borderBottom: i < comments.length - 1 ? "1px solid rgba(148,163,184,0.11)" : "none" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:3 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                        <span style={{ fontSize:11, fontWeight:600, color: c.is_internal ? "#fcd34d" : "#94a3b8" }}>
                          {c.author_name || "System"}
                        </span>
                        {c.is_internal && (
                          <span style={{ fontSize:9, background:"#78350f", color:"var(--badge-amber)", padding:"1px 4px", borderRadius:3 }}>INTERNAL</span>
                        )}
                        {c.author_type === "imported" && (
                          <span style={{ fontSize:9, background:"rgba(148,163,184,0.1)", color:"var(--t-text-faint)", padding:"1px 4px", borderRadius:3 }}>ATERA</span>
                        )}
                      </div>
                      <span style={{ fontSize:10, color:"var(--t-text-faint)" }}>{fmtShort(c.created_at)}</span>
                    </div>
                    <HtmlContent text={c.body} />
                  </div>
                ))}
              </div>
            )}
            <div style={{ display:"flex", gap:6 }}>
              <textarea
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleAddComment(); }}
                rows={2}
                placeholder="Add a comment... (Ctrl+Enter to submit)"
                style={{ flex:1, background:"var(--t-card-input)", border:"1px solid rgba(99,162,255,0.3)", borderRadius:6, padding:"6px 10px", color:"var(--t-text-primary)", fontSize:12, resize:"none" }}
              />
              <button
                onClick={handleAddComment}
                disabled={!newComment.trim() || addingComment}
                style={{
                  background: newComment.trim() ? "linear-gradient(135deg,#4f6fff,#7b61ff)" : "var(--t-input)",
                  border: newComment.trim() ? "none" : "1px solid rgba(148,163,184,0.18)",
                  borderRadius:6, padding:"6px 10px",
                  color: newComment.trim() ? "#fff" : "#56677e",
                  cursor: newComment.trim() ? "pointer" : "not-allowed",
                  fontSize:12, flexShrink:0, fontWeight:600,
                  boxShadow: newComment.trim() ? "0 4px 16px rgba(99,102,241,0.4)" : "none",
                }}
              >
                {addingComment ? "…" : "Add"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {showDev && (
        <SendToDevModal
          ticket={ticket} agents={agents}
          onClose={() => setShowDev(false)}
          onSent={(data) => {
            setShowDev(false);
            setToast({ message:`Sent to dev queue — ${data.assigned_devs?.map(d=>`${d.first_name} ${d.last_name}`).join(", ")} notified`, color:"#7b61ff" });
            getTicket(id).then(r => setTicket(r.data));
          }}
        />
      )}
      {showInfra && (
        <SendToInfraModal
          ticket={ticket} agents={agents}
          onClose={() => setShowInfra(false)}
          onSent={(data) => {
            setShowInfra(false);
            setToast({ message:`Infrastructure project created — team notified`, color:"#0891b2" });
            getTicket(id).then(r => setTicket(r.data));
          }}
        />
      )}
      {showSecureLink && (
        <SecureLinkModal
          ticketId={ticket.id}
          defaultEmail={ticket.submitter_email || ""}
          defaultTitle={ticket.subject ? `Re: ${ticket.subject}` : ""}
          onClose={() => setShowSecureLink(false)}
        />
      )}
      {toast && <Toast message={toast.message} color={toast.color} onDismiss={() => setToast(null)}/>}
    </div>
  );
}
